/**
 * Garde-temps des requêtes de données.
 *
 * Sans timeout, un serveur MUET mais dont la socket reste ouverte ne produit
 * aucune exception : la promesse `fetch` ne se règle jamais, aucun bandeau
 * d'erreur n'apparaît et l'UI attend indéfiniment. C'est un mode de panne
 * INVISIBLE — le plus dangereux. On borne donc toute requête `/api/*` de données.
 *
 * Le délai est GÉNÉREUX : il ne sert pas à détecter la lenteur (purge, effacement
 * en masse, estimation d'archive sur un gros arbre peuvent légitimement durer),
 * seulement à garantir qu'une requête finit TOUJOURS par se régler.
 */
export const API_TIMEOUT_MS = 60_000;

/**
 * Compose le signal d'un appelant avec un garde-temps. Renvoie `undefined` quand
 * aucun des deux n'est disponible (l'appelant passe alors `signal: null`).
 *
 * `AbortSignal.timeout`/`AbortSignal.any` sont standard mais absents de certains
 * environnements (happy-dom selon la version, navigateurs anciens) : on dégrade
 * proprement au lieu de lever — un appel sans garde-temps reste préférable à un
 * crash.
 */
export function withTimeoutSignal(
  signal: AbortSignal | null | undefined,
  timeoutMs: number = API_TIMEOUT_MS,
): AbortSignal | undefined {
  const timeout =
    typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
      ? AbortSignal.timeout(timeoutMs)
      : undefined;

  if (!signal) return timeout;
  if (!timeout) return signal;
  if (typeof AbortSignal.any !== 'function') return signal; // l'annulation de l'appelant prime
  return AbortSignal.any([signal, timeout]);
}
